import { useMemo, useState, useEffect } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_TableOptions,
} from 'material-react-table';
import {
  Typography,
  TextField,
  Box,
  InputAdornment,
  useMediaQuery,
  Pagination,
  IconButton,
} from '@mui/material';
import { MRT_Cell } from 'material-react-table';
import searchIcon from '../../../../assets/icons/Icon.svg';
import add from '../../../../assets/icons/PlusWhiteStroke.svg';
import DeleteIcon from '../../../../assets/icons/delete1.svg';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import { useNavigate } from 'react-router-dom';
import ROUTE from 'packages/novous_web/src/router/Constant';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import AlertToast from 'packages/novous_web/src/common/components/Alert';

interface Company {
  id: string;
  registrationNo: string; // maps to 'crn' from API
  name: string;
  website: string; // maps to 'url' from API
  email: string; // maps to 'contacts.email' from API
}

// Update the type for your table and data
type ColumnObjType = {
  [key: string]: {
    name: string;
    status: boolean;
  };
};

interface GroupOption {
  value: string;
  label: string;
}

const groupOptions: GroupOption[] = [
  { value: 'group-a', label: 'Group A' },
  { value: 'group-b', label: 'Group B' },
];

const shapeOptions: GroupOption[] = [
  { value: 'circle', label: 'Circle' },
  { value: 'rectangle', label: 'Square' },
  { value: 'polygon', label: 'Custom' },
];

export const CompanyScreen: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [data, setData] = useState<Company[]>([]);
  const [filteredData, setFilteredData] = useState<Company[]>([]);
  const [paginatedData, setPaginatedData] = useState<Company[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedGeofence, setSelectedGeofence] = useState<any>(null);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [deleteCompanyId, setDeleteCompanyId] = useState<string>('');
  const isSmallScreen = useMediaQuery('(max-width:850px)');
  const navigate = useNavigate();

  const rowsPerPage = 10;

  // API Hook for fetching Company List
  const {
    data: CompanyListData,
    loading: CompanyListLoading,
    fetchData: fetchCompanyListData,
  } = useAxios<any>(API_ENDPOINTS.users.COMPANY_LIST, 'GET');

  // API Hook for deleting company
  const {
    data: deleteCompanyData,
    error: deleteCompanyError,
    loading: deleteCompanyLoading,
    fetchData: fetchDeleteCompany,
  } = useAxios<any>(`${API_ENDPOINTS.users.COMPANY_DELETE}${deleteCompanyId}`, 'DELETE');

  useEffect(() => {
    fetchCompanyListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger delete API call when deleteCompanyId is set
  useEffect(() => {
    if (deleteCompanyId) {
      fetchDeleteCompany();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteCompanyId]);

  useEffect(() => {
    console.log('CompanyListData changed:', CompanyListData);
    console.log('CompanyListLoading:', CompanyListLoading);

    if (CompanyListData && !CompanyListLoading) {
      console.log('Company List API Response:', CompanyListData);

      // The API response is wrapped in an object with 'data' property
      const apiData = CompanyListData.data || CompanyListData;
      console.log('Extracted API Data:', apiData);
      console.log('Is Array:', Array.isArray(apiData));
      console.log('Length:', apiData?.length);

      // Transform API data to match our interface
      if (Array.isArray(apiData)) {
        const transformedData: Company[] = apiData.map((item: any) => ({
          id: item.id,
          registrationNo: item.crn || '',
          name: item.name || '',
          website: item.url || '',
          email: item.contacts?.email || '',
        }));

        console.log('Setting data:', transformedData);
        setData(transformedData);
        setFilteredData(transformedData);
        console.log('Transformed Company Data length:', transformedData.length);
      } else {
        console.log('Data is not an array:', typeof apiData);
      }
    }
  }, [CompanyListData, CompanyListLoading]);

  // Handle delete company response
  useEffect(() => {
    if (deleteCompanyData && !deleteCompanyLoading) {
      console.log('Delete Company API Response:', deleteCompanyData);

      // Check if the message indicates success or failure
      const message = deleteCompanyData.message;
      const isSuccess = message && !message.toLowerCase().includes('not deleted');

      if (message) {
        setToast({
          open: true,
          message: message,
          type: isSuccess ? 'success' : 'error',
        });
      }

      // Only refresh the company list if deletion was successful
      if (isSuccess) {
        fetchCompanyListData();
      }

      // Clear the deleteCompanyId after handling the response
      setDeleteCompanyId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteCompanyData, deleteCompanyLoading]);

  // Handle delete company errors
  useEffect(() => {
    if (deleteCompanyError) {
      console.error('Delete Company API Error:', deleteCompanyError);
      setToast({
        open: true,
        message: deleteCompanyError || 'Failed to delete company',
        type: 'error',
      });

      // Clear the deleteCompanyId on error
      setDeleteCompanyId('');
    }
  }, [deleteCompanyError]);

  // Add search filtering effect
  useEffect(() => {
    console.log('Filtering data with search query:', searchQuery);
    console.log('Current data length:', data.length);

    if (searchQuery.trim() === '') {
      setFilteredData(data);
    } else {
      const filtered = data.filter(
        (company) =>
          company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          company.registrationNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
          company.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          company.website.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredData(filtered);
    }

    // Reset to first page when search changes
    setCurrentPage(1);
    console.log('Filtered data length:', filteredData.length);
  }, [searchQuery, data]);

  // Pagination effect
  useEffect(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginated = filteredData.slice(startIndex, endIndex);
    setPaginatedData(paginated);
    console.log(
      `Showing page ${currentPage}, rows ${startIndex + 1}-${Math.min(endIndex, filteredData.length)} of ${
        filteredData.length
      }`,
    );
  }, [filteredData, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    console.log('Page changed to:', page);
  };

  const [columnObj, setColumnObj] = useState<ColumnObjType>({
    registrationNo: { name: 'Registration No', status: true },
    name: { name: 'Name', status: true },
    website: { name: 'Website', status: true },
    email: { name: 'Email', status: true },
    actions: { name: 'Action', status: true },
  });

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, geofence: Company) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedGeofence(geofence);
  };

  const handleDeleteCompany = (company: Company) => {
    console.log('Deleting company:', company);

    // Set the company ID to trigger the delete API call with dynamic URL
    setDeleteCompanyId(company.id);

    // Close any open menu
    setAnchorEl(null);
    setSelectedGeofence(null);
  };

  const allColumns: MRT_ColumnDef<Company>[] = useMemo(() => {
    return [
      {
        accessorKey: 'registrationNo',
        header: 'Registration No',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 120,
        muiTableBodyCellProps: {
          sx: { maxWidth: '120px', padding: '8px', overflow: 'hidden' },
        },
        Cell: ({ cell }: { cell: MRT_Cell<Company> }) => (
          <Typography className="truncate" variant="body2" fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 180,
        muiTableBodyCellProps: {
          sx: { maxWidth: '180px', padding: '8px', overflow: 'hidden' },
        },
        Cell: ({ cell }: { cell: MRT_Cell<Company> }) => (
          <Typography className="truncate" variant="body2" fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'website',
        header: 'Website',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 200,
        muiTableBodyCellProps: {
          sx: { maxWidth: '200px', padding: '8px', overflow: 'hidden' },
        },
        Cell: ({ cell }: { cell: MRT_Cell<Company> }) => {
          const url = cell.getValue<string>();
          // Ensure the URL has a protocol for external links
          const href = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
          return (
            <Typography className="truncate" variant="body2" fontWeight={400} noWrap>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1976d2', textDecoration: 'underline' }}
                onClick={(e) => e.stopPropagation()}
              >
                {url}
              </a>
            </Typography>
          );
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
        enableColumnActions: false,
        size: isSmallScreen ? 90 : 200,
        muiTableBodyCellProps: {
          sx: { maxWidth: '200px', padding: '8px', overflow: 'hidden' },
        },
        Cell: ({ cell }: { cell: MRT_Cell<Company> }) => (
          <Typography className="truncate" variant="body2" fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      // {
      //   accessorKey: 'actions',
      //   header: 'Action',
      //   enableColumnActions: false,
      //   size: isSmallScreen ? 90 : 140,
      //   muiTableBodyCellProps: {
      //     sx: { maxWidth: '140px', padding: '8px', overflow: 'hidden' },
      //   },
      //   Cell: ({ row }: { row: MRT_Cell<Company>['row'] }) => (
      //     <div style={{ display: 'flex', alignItems: 'center' }}>
      //       <IconButton
      //         onClick={(e) => {
      //           e.stopPropagation();
      //           handleDeleteCompany(row.original);
      //         }}
      //         disabled={deleteCompanyLoading}
      //       >
      //         <img
      //           src={DeleteIcon}
      //           alt="Delete"
      //           width={24}
      //           height={24}
      //           style={{ cursor: 'pointer', opacity: deleteCompanyLoading ? 0.5 : 1 }}
      //         />
      //       </IconButton>
      //     </div>
      //   ),
      // },
    ].filter((column) => columnObj[column.accessorKey as keyof ColumnObjType]?.status);
  }, [columnObj, isSmallScreen]);

  const columns: MRT_ColumnDef<Company>[] = useMemo(() => {
    return allColumns.filter((column) => {
      const key = column.accessorKey as keyof ColumnObjType;
      return columnObj[key]?.status;
    });
  }, [allColumns, columnObj]);

  const tableOptions: MRT_TableOptions<Company> = {
    columns,
    data: paginatedData,
    enablePagination: false,
    enableColumnFilters: false,
    enableSorting: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableFullScreenToggle: false,
    enableColumnResizing: false,
    enableToolbarInternalActions: false,
    // Show custom message when no data
    renderEmptyRowsFallback: () => (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px" width="100%">
        <Typography variant="h6" color="text.secondary">
          {CompanyListLoading ? 'Loading companies...' : 'No companies found'}
        </Typography>
      </Box>
    ),
    muiTableBodyProps: {
      sx: {
        padding: 0,
        margin: 0,
      },
    },
    muiTableBodyRowProps: ({ staticRowIndex }) => ({
      sx: {
        backgroundColor: staticRowIndex % 2 === 0 ? '#F0EFF8' : 'inherit',
      },
    }),
  };

  console.log('Table data:', paginatedData);
  console.log('Table data length:', paginatedData.length);
  console.log('Loading state:', CompanyListLoading);

  const table = useMaterialReactTable(tableOptions);

  return (
    <>
      <div className="flex flex-col gap-4 w-full">
        <div className="flex justify-between gap-4 sm:flex-row flex-col">
          <div className="flex gap-2">
            <div className="max-w-[400px]">
              <TextField
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search"
                sx={{
                  width: '400px',
                  height: '48px',
                  gap: '12px',
                  borderRadius: '8px',
                  borderWidth: '1px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    height: '48px',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <img src={searchIcon} alt="search" width={16} height={16} />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>

          <div className="flex gap-4 flex-wrap">
            <CustomIconButton
              icon={<img src={add} alt="Add" width={16} height={16} />}
              text="Create Company"
              variant="contained"
              onClick={() => navigate(ROUTE.CreateCompany)}
            />
          </div>
        </div>

        <MaterialReactTable table={table} />

        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      </div>

      <AlertToast
        open={toast.open}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        type={toast.type as any}
      />
    </>
  );
};

export default CompanyScreen;
